import React, { useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { groupBy } from "lodash";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardHeader,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography, IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { AddCircleOutline, Close, ExpandMore } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

import { searchVariables } from "@/api/list";
import { DialogComponentProps, useAudienceDispatch } from "@/context";
import { Variable } from "@/types";
import VariableSearchDialogSkeleton from "./VariableSearchDialogSkeleton";
import { HelpBox, SearchBox } from "@/components/atoms";

type VariableSearchDialogProps = DialogComponentProps<"variableSearch">;

const VariableSearchDialog: React.FC<VariableSearchDialogProps> = ({
  open,
  onClose,
  queryGroupId,
  selectedVariables,
}) => {
  const theme = useTheme();
  const dispatch = useAudienceDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const [searchText, setSearchText] = useState<string>("");
  const [searchResults, setSearchResults] = useState<Variable[]>([]);
  const [currentSelection, setCurrentSelection] = useState<Variable[]>(selectedVariables);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (searchText === "") {
      setSearchResults([]);
    } else {
      handleSearch(searchText);
    }
  }, [searchText]);

  // Group results by tag_type
  const groupedResults = useMemo(() => {
    const groupData = groupBy(searchResults, 'tag_type');
    const groups = Object.keys(groupData);
    return { groups, groupData }
  }, [searchResults]);

  // Check if a variable is selected
  const isSelected = (variable: Variable) => {
    return currentSelection.some((v) => v.tag_id === variable.tag_id);
  };

  // Handle chip click to toggle selection
  const onSelect = (variable: Variable) => {
    let updatedVariables: Variable[];

    if (isSelected(variable)) {
      // Remove from selection
      updatedVariables = currentSelection.filter(
        (v) => v.tag_id !== variable.tag_id
      );
    } else {
      // Add to selection
      updatedVariables = [...currentSelection, variable];
    }

    setCurrentSelection(updatedVariables);
    dispatch({
      type: "CHANGE_GROUP_VARIABLES",
      payload: { id: queryGroupId, variables: updatedVariables },
    });
  };

  const onSelectMulti = (variables: Variable[]) => {
    let updatedVariables = [...currentSelection];

    variables.forEach(variable => {
      if (!isSelected(variable)) {
        updatedVariables = [...updatedVariables, variable]
      }
    })
    setCurrentSelection(updatedVariables);
    dispatch({
      type: "CHANGE_GROUP_VARIABLES",
      payload: { id: queryGroupId, variables: updatedVariables },
    });

  }

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSearchText(event.target.value || "");

  const handleSearch = async (searchTerm: string) => {
    const accessToken = await getAccessTokenSilently();
    const client = process.env.REACT_APP_CLIENT || "core";

    if (searchTerm.length < 2) return;
    if (!accessToken) return;

    setLoading(true);
    const { data, error } = await searchVariables(
      accessToken,
      searchTerm,
      client
    );

    if (data) {
      setSearchResults(data);
      setLoading(false);
    }

    if (error) {
      // onError(error.message);
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      sx={{
        maxHeight: "80vh",
      }}
      fullWidth
      aria-labelledby="variable-search-dialog"
    >
      <DialogTitle>
        <Typography>Search Variables</Typography>
      </DialogTitle>
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
        }}
      >
        <Close />
      </IconButton>

      <DialogContent
        sx={{
          height: "60vh",
          overflowY: "auto",
        }}
      >
        <Grid container size={12} spacing={3} pt={1}>
          <Grid size={12}>
            <SearchBox
              value={searchText}
              onSearch={handleSearchTextChange}
              fullWidth={true}
              placeholder="Search for variables or categories"
            />
          </Grid>
          {loading ? (
            <Grid size={12}>
              <VariableSearchDialogSkeleton />
            </Grid>
          ) : (
            groupedResults.groups.map((group, groupIdx) => {

              const categoryData = groupBy(groupedResults.groupData[group], 'tag_category');
              const categories = Object.keys(categoryData);

              return (
                <Grid key={groupIdx} size={12}>
                  <Card >
                    <CardHeader
                      title={group}
                      slotProps={{ title: { variant: "body1" } }}
                    />
                    <CardContent>
                      {categories.map((category, categoryIdx) => {
                        return (
                          <Accordion
                            key={`${groupIdx}-${categoryIdx}`}
                            defaultExpanded
                            slotProps={{
                              root: {
                                elevation: 3
                              }
                            }}
                          >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                              <Typography>{category}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Box pb={4}>
                                {categoryData[category].map((variable, variableIdx) => (
                                  <Chip
                                    key={`${groupIdx}-${categoryIdx}-${variableIdx}`}
                                    size="small"
                                    color={isSelected(variable) ? "secondary" : "default"}
                                    label={variable.tag_name}
                                    onClick={() => onSelect(variable)}
                                    sx={{ m: 0.5 }}
                                  />
                                ))}
                              </Box>
                              <HelpBox title="Add all variables to the Query Group">
                                <IconButton
                                  sx={{
                                    position: "absolute",
                                    bottom: 8,
                                    right: 12,
                                    zIndex: 1,
                                    p: 0.5,
                                    borderRadius: "6px",
                                    color: theme.palette.divider,
                                  }}
                                  onClick={() => onSelectMulti(categoryData[category])}
                                >
                                  <AddCircleOutline fontSize="small" />
                                </IconButton>
                              </HelpBox>
                            </AccordionDetails>
                          </Accordion>
                        )}
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              )
            })
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default VariableSearchDialog;
